import {
  nutritionTableRowPadsExtraHeightMm,
  nutritionTableRulesExtraHeightMm,
} from '../prepackaged-block-line';
import {
  collidesInLayout,
  findPrepackagedLabelEditorPreset,
  type PrepackagedLabelEditorBlockId,
  type PrepackagedLabelEditorPresetId,
  type PrepackagedLabelLayoutAssetKind,
  type PrepackagedLabelLayoutAssetV2,
  type PrepackagedLabelLayoutBlockV2,
  type PrepackagedLabelLayoutDocumentV2,
} from '../prepackaged-label-editor';
import { collisionRectMm } from './rotated-rect';

export { LAYOUT_OVERFLOW_MARGIN_MM } from './layout-canvas.constants';

export const DEFAULT_LAYOUT_DPI = 96;
export const DEFAULT_SNAP_STEP_MM = 0.5;
export const DEFAULT_MIN_BLOCK_WIDTH_MM = 8;
export const DEFAULT_MIN_BLOCK_HEIGHT_MM = 4;
export const DEFAULT_BLOCK_GAP_MM = 2;
export const DEFAULT_MIN_ASSET_WIDTH_MM = 4;
export const DEFAULT_MIN_ASSET_HEIGHT_MM = 4;

/**
 * Geometry floor per asset kind. The defaults above stay the *generic* asset
 * floor (TEXT/LOGO/GLYPH/CROP live here too); LINE is a hairline, a RECT or
 * ELLIPSE is visible from 1mm, while a scannable EAN needs 20×10mm and a QR
 * never helps below 8×8mm. `clampGeometry` callers and the geometry
 * validators read this table instead of guessing one number for every kind.
 */
export const MIN_ASSET_SIZE_MM: Record<
  PrepackagedLabelLayoutAssetKind,
  { widthMm: number; heightMm: number }
> = {
  LINE: { widthMm: 0.2, heightMm: 0.2 },
  RECT: { widthMm: 1, heightMm: 1 },
  ELLIPSE: { widthMm: 1, heightMm: 1 },
  BARCODE: { widthMm: 20, heightMm: 10 },
  QR: { widthMm: 8, heightMm: 8 },
  TEXT: { widthMm: 4, heightMm: 4 },
  LOGO: { widthMm: 4, heightMm: 4 },
  GLYPH: { widthMm: 4, heightMm: 4 },
  CROP: { widthMm: 4, heightMm: 4 },
};

const minAssetSizeFor = (
  kind: PrepackagedLabelLayoutAssetKind,
  options: Pick<LayoutSolverOptions, 'minAssetWidthMm' | 'minAssetHeightMm'>,
): { widthMm: number; heightMm: number } => ({
  widthMm: options.minAssetWidthMm ?? MIN_ASSET_SIZE_MM[kind].widthMm,
  heightMm: options.minAssetHeightMm ?? MIN_ASSET_SIZE_MM[kind].heightMm,
});

/**
 * The rect an element collides through: identical to its geometry unless the
 * element is a rotated asset, in which case it is the AABB of the rotated box
 * (see `collisionRectMm`). Blocks never rotate, so they pass through as-is.
 */
const collisionRectOf = (element: { geometry: LayoutRectMm; rotationDeg?: number }): LayoutRectMm =>
  collisionRectMm(element.geometry, element.rotationDeg ?? 0);
export const MAX_LAYOUT_PAGE_WIDTH_MM = 1000;
export const MAX_LAYOUT_PAGE_HEIGHT_MM = 1000;
export const MIN_LAYOUT_SNAP_STEP_MM = 0.1;
export const MAX_LAYOUT_GRID_CELLS = 250_000;

/**
 * `expandPreset` walks this ladder of page margins: 4mm is the default paper
 * margin a fresh label presents with. A preset's fixed block set cannot fit
 * some small labels even flush-packed, so `expandPreset` steps down through
 * the ladder — shrinking the margin, never opening gaps between blocks — and
 * keeps the first step whose geometry is valid (in-page, non-overlapping).
 * If even the finest margin does not fit, it returns that attempt as a
 * best-effort layout instead of throwing — `expandPreset` has never thrown,
 * and callers that need a guaranteed-legal document already re-solve the
 * result (`makePresetDocument` → `solveLayout`).
 */
export const PRESET_GRID_STEP_LADDER_MM = [4, 2, 1, DEFAULT_SNAP_STEP_MM] as const;

export interface LayoutRectMm {
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
}

export interface LayoutPageMm {
  widthMm: number;
  heightMm: number;
}

export interface LayoutSolverOptions {
  dpi?: number;
  snapStepMm?: number;
  minWidthMm?: number;
  minHeightMm?: number;
  blockGapMm?: number;
  minAssetWidthMm?: number;
  minAssetHeightMm?: number;
  protectedAreas?: readonly LayoutRectMm[];
  placementMode?: 'SEARCH' | 'STRICT';
  /** How far outside the page an element may sit. 0 (the default) keeps every
   *  element fully inside the label. See `LAYOUT_OVERFLOW_MARGIN_MM`. */
  overflowMarginMm?: number;
  /**
   * Maps a candidate geometry to the rectangle it collides through. Solver
   * callers pass `collisionRectMm(geometry, rotationDeg)` for a rotated
   * asset; unset means the identity (blocks, unrotated assets).
   */
  toCollisionRect?: (geometry: LayoutRectMm) => LayoutRectMm;
  /**
   * Modalità libera con livelli (draw.io-style): i record sovrapposti sono
   * ammessi e l'ordine di disegno è responsabilità dell'operatore (z-order).
   * Pagina, minimi di dimensione e margini restano enforce — solo il vincolo
   * di non-sovrapposizione si rilascia. La guidata non lo passa mai.
   */
  allowOverlaps?: boolean;
}

export class LayoutUnsatisfiableError extends Error {
  readonly code = 'LAYOUT_UNSATISFIABLE' as const;

  constructor(
    readonly blockId: PrepackagedLabelEditorBlockId,
    readonly reason: string,
  ) {
    super(`unsatisfiable layout for ${blockId}: ${reason}`);
    this.name = 'LayoutUnsatisfiableError';
  }
}

export class LayoutInputLimitError extends Error {
  readonly code = 'LAYOUT_INPUT_LIMIT' as const;

  constructor(
    readonly blockId: PrepackagedLabelEditorBlockId,
    readonly reason: string,
  ) {
    super(`layout input exceeds bounded solver limits for ${blockId}: ${reason}`);
    this.name = 'LayoutInputLimitError';
  }
}

const round = (value: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export function mmToPx(mm: number, dpi = DEFAULT_LAYOUT_DPI): number {
  if (!Number.isFinite(mm) || !Number.isFinite(dpi) || dpi <= 0) {
    return 0;
  }
  return round((mm / 25.4) * dpi, 2);
}

export function pxToMm(px: number, dpi = DEFAULT_LAYOUT_DPI): number {
  if (!Number.isFinite(px) || !Number.isFinite(dpi) || dpi <= 0) {
    return 0;
  }
  return round((px / dpi) * 25.4, 2);
}

export function snapMm(value: number, stepMm = DEFAULT_SNAP_STEP_MM): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (!Number.isFinite(stepMm) || stepMm <= 0) {
    return round(value);
  }
  return round(Math.round(value / stepMm) * stepMm, 2);
}

const right = (rect: LayoutRectMm): number => rect.xMm + rect.widthMm;
const bottom = (rect: LayoutRectMm): number => rect.yMm + rect.heightMm;

const normalizedGapMm = (gapMm: number | undefined): number =>
  Number.isFinite(gapMm) ? Math.max(0, gapMm ?? 0) : 0;

export function rectanglesOverlap(a: LayoutRectMm, b: LayoutRectMm): boolean {
  return a.xMm < right(b) && right(a) > b.xMm && a.yMm < bottom(b) && bottom(a) > b.yMm;
}

export function clampGeometry(
  geometry: LayoutRectMm,
  page: LayoutPageMm,
  options: Pick<
    LayoutSolverOptions,
    'minWidthMm' | 'minHeightMm' | 'snapStepMm' | 'overflowMarginMm'
  > = {},
): LayoutRectMm {
  const overflowMm = Math.max(0, options.overflowMarginMm ?? 0);
  const minWidth = Math.max(0.01, options.minWidthMm ?? DEFAULT_MIN_BLOCK_WIDTH_MM);
  const minHeight = Math.max(0.01, options.minHeightMm ?? DEFAULT_MIN_BLOCK_HEIGHT_MM);
  // The parking margin buys an element somewhere to *sit*, not licence to grow
  // past the label: a box wider than the page could never print whole.
  const maxWidth = page.widthMm;
  const maxHeight = page.heightMm;
  const width = Math.min(maxWidth, Math.max(minWidth, geometry.widthMm));
  const height = Math.min(maxHeight, Math.max(minHeight, geometry.heightMm));
  const step = options.snapStepMm ?? DEFAULT_SNAP_STEP_MM;
  const hasGrid = Number.isFinite(step) && step > 0;
  const onGrid = (value: number, minimum: number, maximum: number, fallback: number): number => {
    if (!hasGrid) {
      return round(Math.min(maximum, Math.max(minimum, value)));
    }
    const minimumOnGrid = round(Math.ceil((minimum - Number.EPSILON) / step) * step);
    const maximumOnGrid = round(Math.floor((maximum + Number.EPSILON) / step) * step);
    if (maximumOnGrid < minimumOnGrid) {
      return fallback;
    }
    return round(Math.min(maximumOnGrid, Math.max(minimumOnGrid, snapMm(value, step))));
  };
  const snappedWidth = onGrid(
    width,
    minWidth,
    maxWidth,
    round(Math.min(maxWidth, Math.max(minWidth, width))),
  );
  const snappedHeight = onGrid(
    height,
    minHeight,
    maxHeight,
    round(Math.min(maxHeight, Math.max(minHeight, height))),
  );
  // A box wider than the page plus both margins has no legal x at all; pinning
  // it to the left margin keeps the result deterministic instead of empty.
  const maxX = page.widthMm + overflowMm - snappedWidth;
  const maxY = page.heightMm + overflowMm - snappedHeight;
  return {
    xMm: onGrid(geometry.xMm, -overflowMm, maxX, round(-overflowMm)),
    yMm: onGrid(geometry.yMm, -overflowMm, maxY, round(-overflowMm)),
    widthMm: round(snappedWidth),
    heightMm: round(snappedHeight),
  };
}

/**
 * Lines a block has to hold before the operator touches anything. These are
 * not aesthetic guesses: the nutrition declaration is the reference quantity
 * plus the eight nutrients of Allegato XV, each on its own row because
 * art. 34 §2 Reg. (UE) 1169/2011 asks for a table; the product name is the
 * fancy name plus the legal denomination that art. 17 §4 keeps mandatory
 * underneath it. A block sized below its own mandatory content overflows on
 * the first correct label.
 */
const MANDATORY_LINES: Partial<Record<PrepackagedLabelEditorBlockId, number>> = {
  PRODUCT_NAME: 3,
  INGREDIENTS: 5,
  NUTRITION: 9,
};

const DEFAULT_BLOCK_LINES = 2.5;
const LINE_HEIGHT_RATIO = 1.2;

/**
 * Ratio between a font's x-height and its body size for the print face. The
 * PDF renderer floors every block at the body size this produces, so the
 * preset has to size its boxes with the same number: estimating it separately
 * (it used to assume 1.8) made every box a little too short for the content
 * the renderer then measured, and the label refused to print.
 */
export const PRINT_FONT_X_HEIGHT_RATIO = 0.523;

/**
 * The legally smallest body a block may be set in: art. 13 §2 Reg. (UE)
 * 1169/2011 fixes the x-height, and this is the font size that produces it.
 * The nutrition table is set here rather than at the preset's reading size —
 * that is how a real label fits nine mandatory rows without swallowing the
 * page, and the alternative (a box tall enough for nine rows of body text)
 * left no room on a 100x150mm label for anything else, decorations included.
 */
const legalFloorFontSizeMm = (minXHeightMm: number): number =>
  minXHeightMm / PRINT_FONT_X_HEIGHT_RATIO;

const blockFontSizeMm = (
  id: PrepackagedLabelEditorBlockId,
  baseFontSizeMm: number,
  minXHeightMm: number,
): number =>
  id === 'NUTRITION'
    ? legalFloorFontSizeMm(minXHeightMm)
    : Math.max(baseFontSizeMm, legalFloorFontSizeMm(minXHeightMm));

/**
 * Of `MANDATORY_LINES.NUTRITION`, the first line is the FLOW heading
 * ("Dichiarazione nutrizionale · valori medi per …") and only the remaining
 * nutrient rows print inside the ruled table — see `nutritionLines`
 * (`prepackaged-label-block-lines.ts`), which emits one FLOW line before the
 * ROW lines. The pads and hairlines below are charged per table row, so the
 * heading must not be counted among them.
 */
const NUTRITION_HEADING_LINES = 1;

/**
 * Art. 34 §2: the nutrient rows print as a ruled table, and the painter
 * charges every per-row pad and boundary hairline to the block's height
 * (`prepackaged-label-block-painter.ts`), exactly as the editor's content
 * floor does (`blockLinesContentFloorMm`). A preset that sizes the box on
 * bare text lines hands the operator a default label whose nutrition block
 * the print gate immediately rejects with RENDER_OVERFLOW. The derivation
 * lives in `prepackaged-block-line.ts` and is reused here, not restated.
 */
const nutritionTableChromeMm = (): number => {
  const tableRowCount = (MANDATORY_LINES.NUTRITION ?? 0) - NUTRITION_HEADING_LINES;
  return (
    nutritionTableRowPadsExtraHeightMm(tableRowCount) +
    nutritionTableRulesExtraHeightMm(tableRowCount)
  );
};

const blockHeight = (
  id: PrepackagedLabelEditorBlockId,
  baseFontSizeMm: number,
  minXHeightMm: number,
): number => {
  const lines = MANDATORY_LINES[id] ?? DEFAULT_BLOCK_LINES;
  const textHeightMm =
    lines * blockFontSizeMm(id, baseFontSizeMm, minXHeightMm) * LINE_HEIGHT_RATIO;
  return id === 'NUTRITION' ? textHeightMm + nutritionTableChromeMm() : textHeightMm;
};

const blockTypography = (
  id: PrepackagedLabelEditorBlockId,
  baseFontSizeMm: number,
  minXHeightMm: number,
) => ({
  fontSizeMm: blockFontSizeMm(id, baseFontSizeMm, minXHeightMm),
  fontWeight: 400,
  lineHeight: LINE_HEIGHT_RATIO,
  align: 'LEFT' as const,
});

/**
 * Builds one candidate expansion of `presetId` on `page`. Compact by
 * contract: blocks fill the printable width exactly (`gridStepMm` margin per
 * side, no column gap), stack flush one under the other with no breathing
 * space, and each block is exactly as tall as its mandatory content
 * (`blockHeight`, ceiling to the 0.5mm editor snap so a block is never
 * smaller than what it has to print). Whitespace between blocks is the
 * operator's own decision on the canvas, never a default.
 */
function buildPresetCandidate(
  presetId: PrepackagedLabelEditorPresetId,
  page: LayoutPageMm,
  gridStepMm: number,
  options: Pick<LayoutSolverOptions, 'minWidthMm' | 'minHeightMm'>,
): PrepackagedLabelLayoutDocumentV2 {
  const preset = findPrepackagedLabelEditorPreset(presetId);
  const margin = gridStepMm;
  const columnGap = 0;
  const gap = 0;
  const columns = preset.columns;
  const columnWidth = (page.widthMm - 2 * margin - (columns - 1) * columnGap) / columns;
  const heights = preset.blockOrder.map((id) => {
    const rawHeight = blockHeight(
      id,
      preset.typography.baseFontSizeMm,
      preset.typography.minXHeightMm,
    );
    return Math.ceil(rawHeight / DEFAULT_SNAP_STEP_MM) * DEFAULT_SNAP_STEP_MM;
  });
  const columnY = Array.from({ length: columns }, () => margin);
  const blocks: PrepackagedLabelLayoutBlockV2[] = preset.blockOrder.map((id, index) => {
    const column = index % columns;
    const height = heights[index] ?? gridStepMm;
    const geometry = clampGeometry(
      {
        xMm: margin + column * (columnWidth + columnGap),
        yMm: columnY[column] ?? margin,
        widthMm: columnWidth,
        heightMm: height,
      },
      page,
      {
        // Snap 0: le geometrie della candidata sono già esatte (altezza =
        // contenuto arrotondata a 0,5mm, posizioni derivate) — uno snap
        // aggiuntivo sposterebbe la seconda colonna creando sub-mm di
        // gap/overlap tra le colonne.
        snapStepMm: 0,
        minWidthMm: options.minWidthMm,
        minHeightMm: options.minHeightMm,
      },
    );
    columnY[column] = geometry.yMm + geometry.heightMm + gap;
    return {
      id,
      token: id,
      required: true,
      visible: true,
      geometry,
      typography: blockTypography(
        id,
        preset.typography.baseFontSizeMm,
        preset.typography.minXHeightMm,
      ),
    };
  });
  return {
    schemaVersion: 2,
    widthMm: page.widthMm,
    heightMm: page.heightMm,
    presetId,
    typography: { ...preset.typography },
    blocks,
    assets: [],
  };
}

/**
 * Creates a fresh document from a frozen preset. The same input always
 * yields the same output.
 *
 * Tries `PRESET_GRID_STEP_LADDER_MM` from coarsest (4mm, the editor default)
 * to finest, and returns the first candidate whose geometry is valid — every
 * block in-page and non-overlapping, per `validateLayoutDocumentGeometry`.
 * A preset's block set is fixed regardless of label size, so on a small
 * enough label even the finest step can be unsatisfiable (e.g. a 7-block
 * single-column preset on a 60×40mm label); `expandPreset` has never thrown,
 * so rather than force an invalid 4mm layout or start throwing, it falls
 * back to returning the finest-step attempt as a best-effort layout.
 */
export function expandPreset(
  presetId: PrepackagedLabelEditorPresetId,
  page: LayoutPageMm,
  options: Pick<LayoutSolverOptions, 'minWidthMm' | 'minHeightMm'> = {},
): PrepackagedLabelLayoutDocumentV2 {
  let finestAttempt: PrepackagedLabelLayoutDocumentV2 | undefined;
  for (const gridStepMm of PRESET_GRID_STEP_LADDER_MM) {
    const candidate = buildPresetCandidate(presetId, page, gridStepMm, options);
    finestAttempt = candidate;
    try {
      validateLayoutDocumentGeometry(candidate, {
        minWidthMm: options.minWidthMm,
        minHeightMm: options.minHeightMm,
        snapStepMm: gridStepMm,
      });
      return candidate;
    } catch (error) {
      if (error instanceof LayoutUnsatisfiableError || error instanceof LayoutInputLimitError) {
        continue;
      }
      throw error;
    }
  }
  return finestAttempt as PrepackagedLabelLayoutDocumentV2;
}

const legalAgainst = (
  geometry: LayoutRectMm,
  obstacles: readonly LayoutRectMm[],
  blockGapMm = 0,
): boolean => {
  const gap = normalizedGapMm(blockGapMm);
  return obstacles.every((obstacle) => {
    const expandedObstacle = {
      xMm: obstacle.xMm - gap,
      yMm: obstacle.yMm - gap,
      widthMm: obstacle.widthMm + gap * 2,
      heightMm: obstacle.heightMm + gap * 2,
    };
    return !rectanglesOverlap(geometry, expandedObstacle);
  });
};

const assertBoundedSearch = (
  page: LayoutPageMm,
  snapStepMm: number,
  blockId: PrepackagedLabelEditorBlockId,
): void => {
  if (
    !Number.isFinite(page.widthMm) ||
    !Number.isFinite(page.heightMm) ||
    page.widthMm <= 0 ||
    page.heightMm <= 0 ||
    page.widthMm > MAX_LAYOUT_PAGE_WIDTH_MM ||
    page.heightMm > MAX_LAYOUT_PAGE_HEIGHT_MM
  ) {
    throw new LayoutInputLimitError(
      blockId,
      `page must be finite and at most ${MAX_LAYOUT_PAGE_WIDTH_MM}×${MAX_LAYOUT_PAGE_HEIGHT_MM}mm`,
    );
  }
  if (!Number.isFinite(snapStepMm) || snapStepMm < MIN_LAYOUT_SNAP_STEP_MM) {
    throw new LayoutInputLimitError(
      blockId,
      `snap step must be at least ${MIN_LAYOUT_SNAP_STEP_MM}mm`,
    );
  }
  const columns = Math.ceil(page.widthMm / snapStepMm) + 1;
  const rows = Math.ceil(page.heightMm / snapStepMm) + 1;
  if (
    columns > MAX_LAYOUT_GRID_CELLS ||
    rows > MAX_LAYOUT_GRID_CELLS ||
    columns * rows > MAX_LAYOUT_GRID_CELLS
  ) {
    throw new LayoutInputLimitError(
      blockId,
      `snapped search grid ${columns}×${rows} exceeds ${MAX_LAYOUT_GRID_CELLS} cells`,
    );
  }
};

/** The page plus the parking margin on every side: the area an element may
 *  legally occupy while the operator is still composing. */
const canvasBounds = (page: LayoutPageMm, overflowMm: number): LayoutPageMm => ({
  widthMm: page.widthMm + overflowMm * 2,
  heightMm: page.heightMm + overflowMm * 2,
});

const assertGridCanFitMinimum = (
  page: LayoutPageMm,
  snapStepMm: number,
  minWidthMm: number,
  minHeightMm: number,
  blockId: PrepackagedLabelEditorBlockId,
): void => {
  const minimumGridWidth = Math.ceil((minWidthMm - Number.EPSILON) / snapStepMm) * snapStepMm;
  const minimumGridHeight = Math.ceil((minHeightMm - Number.EPSILON) / snapStepMm) * snapStepMm;
  if (minimumGridWidth > page.widthMm || minimumGridHeight > page.heightMm) {
    throw new LayoutUnsatisfiableError(
      blockId,
      `no ${snapStepMm}mm grid cell satisfies the minimum ${minWidthMm}×${minHeightMm}mm geometry`,
    );
  }
};

const geometryFitsCanvas = (
  geometry: LayoutRectMm,
  page: LayoutPageMm,
  minWidthMm: number,
  minHeightMm: number,
  overflowMm: number,
): boolean =>
  Number.isFinite(geometry.xMm) &&
  Number.isFinite(geometry.yMm) &&
  Number.isFinite(geometry.widthMm) &&
  Number.isFinite(geometry.heightMm) &&
  geometry.xMm >= -overflowMm - Number.EPSILON &&
  geometry.yMm >= -overflowMm - Number.EPSILON &&
  geometry.widthMm >= minWidthMm &&
  geometry.heightMm >= minHeightMm &&
  right(geometry) <= page.widthMm + overflowMm + Number.EPSILON &&
  bottom(geometry) <= page.heightMm + overflowMm + Number.EPSILON;

const acceptExactGeometry = ({
  geometry,
  collision,
  page,
  minWidthMm,
  minHeightMm,
  overflowMm,
  accepted,
  gap,
  blockId,
  reason,
}: {
  geometry: LayoutRectMm;
  /** The rectangle this element collides through (identity for unrotated ones). */
  collision?: LayoutRectMm;
  page: LayoutPageMm;
  minWidthMm: number;
  minHeightMm: number;
  overflowMm: number;
  accepted: LayoutRectMm[];
  gap: number;
  blockId: PrepackagedLabelEditorBlockId;
  reason: string;
}): void => {
  if (
    !geometryFitsCanvas(geometry, page, minWidthMm, minHeightMm, overflowMm) ||
    !legalAgainst(collision ?? geometry, accepted, gap)
  ) {
    throw new LayoutUnsatisfiableError(blockId, reason);
  }
  accepted.push(collision ?? geometry);
};

const participatesInLayout = (element: { required: boolean; visible: boolean }): boolean =>
  element.required || element.visible;

/** Validates the incoming geometry exactly, without snapping or repairing legacy coordinates. */
export function validateLayoutDocumentGeometry(
  document: PrepackagedLabelLayoutDocumentV2,
  options: LayoutSolverOptions = {},
): void {
  const page = { widthMm: document.widthMm, heightMm: document.heightMm };
  const fallbackBlockId = document.blocks[0]?.id ?? 'CUSTOM_SECTIONS';
  assertBoundedSearch(page, options.snapStepMm ?? DEFAULT_SNAP_STEP_MM, fallbackBlockId);
  const gap = options.blockGapMm ?? 0;
  const overflowMm = Math.max(0, options.overflowMarginMm ?? 0);
  const accepted: LayoutRectMm[] = [...(options.protectedAreas ?? [])];
  // Con livelli (FREE) il vincolo di non-sovrapposizione si rilascia: restano
  // i bordi pagina e i minimi di dimensione, che acceptExactGeometry continua
  // a enforce mentre la lista `accepted` non viene più alimentata.
  const overlapsAllowed = options.allowOverlaps === true;
  for (const block of document.blocks.filter(participatesInLayout)) {
    acceptExactGeometry({
      geometry: block.geometry,
      collision: collisionRectOf(block),
      page,
      minWidthMm: options.minWidthMm ?? DEFAULT_MIN_BLOCK_WIDTH_MM,
      minHeightMm: options.minHeightMm ?? DEFAULT_MIN_BLOCK_HEIGHT_MM,
      overflowMm,
      accepted: overlapsAllowed ? [] : accepted,
      gap,
      blockId: block.id,
      reason: `current geometry is outside or overlaps the ${page.widthMm}×${page.heightMm}mm page`,
    });
  }
  // Decorative shapes sit under the blocks by design: they never collide and
  // are never validated against the accepted rects (spec §5.1).
  for (const asset of (document.assets ?? []).filter(
    (asset) => participatesInLayout(asset) && collidesInLayout(asset),
  )) {
    const minSize = minAssetSizeFor(asset.kind, options);
    acceptExactGeometry({
      geometry: asset.geometry,
      collision: collisionRectOf(asset),
      page,
      minWidthMm: minSize.widthMm,
      minHeightMm: minSize.heightMm,
      overflowMm,
      accepted: overlapsAllowed ? [] : accepted,
      gap,
      blockId: fallbackBlockId,
      reason: `current geometry is outside or overlaps for asset ${asset.assetId}`,
    });
  }
}

export function findLegalPlacement(
  source: LayoutRectMm,
  page: LayoutPageMm,
  obstacles: readonly LayoutRectMm[],
  options: Pick<
    LayoutSolverOptions,
    | 'minWidthMm'
    | 'minHeightMm'
    | 'snapStepMm'
    | 'blockGapMm'
    | 'placementMode'
    | 'overflowMarginMm'
    | 'toCollisionRect'
  >,
): LayoutRectMm | null {
  const gap = normalizedGapMm(options.blockGapMm);
  const toCollision = options.toCollisionRect ?? ((geometry: LayoutRectMm) => geometry);
  const candidates: LayoutRectMm[] = [source];
  for (const obstacle of obstacles) {
    candidates.push(
      { ...source, yMm: obstacle.yMm - source.heightMm - gap },
      { ...source, yMm: bottom(obstacle) + gap },
      { ...source, xMm: obstacle.xMm - source.widthMm - gap },
      { ...source, xMm: right(obstacle) + gap },
    );
  }
  const normalizedSource = clampGeometry(source, page, options);
  if (options.placementMode === 'STRICT') {
    return legalAgainst(toCollision(normalizedSource), obstacles, gap) ? normalizedSource : null;
  }
  const step = options.snapStepMm ?? DEFAULT_SNAP_STEP_MM;
  const maxX = Math.max(0, page.widthMm - normalizedSource.widthMm);
  const maxY = Math.max(0, page.heightMm - normalizedSource.heightMm);
  const axisPositions = (max: number): number[] => {
    if (!Number.isFinite(step) || step <= 0) {
      return [0, max];
    }
    const values: number[] = [];
    for (let value = 0; value <= max + Number.EPSILON; value += step) {
      values.push(round(Math.min(max, value)));
    }
    if (values.at(-1) !== round(max)) {
      values.push(round(max));
    }
    return values;
  };
  for (const yMm of axisPositions(maxY)) {
    for (const xMm of axisPositions(maxX)) {
      candidates.push({ ...normalizedSource, xMm, yMm });
    }
  }
  const seen = new Set<string>();
  for (const candidate of candidates) {
    const clamped = clampGeometry(candidate, page, options);
    const key = `${clamped.xMm}:${clamped.yMm}:${clamped.widthMm}:${clamped.heightMm}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    if (legalAgainst(toCollision(clamped), obstacles, gap)) {
      return clamped;
    }
  }
  return null;
}

/**
 * How far an asset the label has no room for may be scaled down before the
 * solver gives up. Mandatory blocks always win over a decorative asset, but
 * an asset the operator just added must not vanish either: a logo placed at
 * up to half its requested size is still a logo the operator can see, move
 * and resize on the canvas, while one shrunk to the 4mm minimum would be a
 * placement in name only. Below this ratio the solver refuses instead
 * (`LayoutUnsatisfiableError`), and the assets panel tells the operator the
 * label has no room and what to do about it.
 */
export const ASSET_SHRINK_FLOOR_RATIO = 0.5;

/**
 * A smaller placement for an asset whose requested size fits nowhere:
 * candidates keep the source's aspect ratio and step down the grid to
 * `ASSET_SHRINK_FLOOR_RATIO` of the requested height. Returns the first legal
 * placement, or null when even the smallest candidate has no room.
 */
function findShrunkAssetPlacement(
  source: LayoutRectMm,
  page: LayoutPageMm,
  obstacles: readonly LayoutRectMm[],
  options: Pick<
    LayoutSolverOptions,
    | 'minWidthMm'
    | 'minHeightMm'
    | 'snapStepMm'
    | 'blockGapMm'
    | 'overflowMarginMm'
    | 'toCollisionRect'
  >,
): LayoutRectMm | null {
  const stepMm = options.snapStepMm ?? DEFAULT_SNAP_STEP_MM;
  const gridStepMm = Number.isFinite(stepMm) && stepMm > 0 ? stepMm : DEFAULT_SNAP_STEP_MM;
  const minWidthMm = Math.max(0.01, options.minWidthMm ?? DEFAULT_MIN_ASSET_WIDTH_MM);
  const minHeightMm = Math.max(0.01, options.minHeightMm ?? DEFAULT_MIN_ASSET_HEIGHT_MM);
  if (source.heightMm <= 0 || source.widthMm <= 0) {
    return null;
  }
  const aspect = source.widthMm / source.heightMm;
  const floorHeightMm = Math.max(minHeightMm, source.heightMm * ASSET_SHRINK_FLOOR_RATIO);
  for (
    let heightMm = snapMm(source.heightMm, gridStepMm) - gridStepMm;
    heightMm >= floorHeightMm - Number.EPSILON;
    heightMm -= gridStepMm
  ) {
    const widthMm = Math.max(minWidthMm, snapMm(heightMm * aspect, gridStepMm));
    const placement = findLegalPlacement(
      { ...source, widthMm: round(widthMm), heightMm: round(heightMm) },
      page,
      obstacles,
      options,
    );
    if (placement) {
      return placement;
    }
  }
  return null;
}

/** Normalizes a document for persistence/printing without mutating the caller's object. */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: block and asset constraints are intentionally solved in one deterministic pass.
export function solveLayout(
  document: PrepackagedLabelLayoutDocumentV2,
  options: LayoutSolverOptions = {},
): PrepackagedLabelLayoutDocumentV2 {
  const page = { widthMm: document.widthMm, heightMm: document.heightMm };
  const minWidthMm = options.minWidthMm ?? DEFAULT_MIN_BLOCK_WIDTH_MM;
  const minHeightMm = options.minHeightMm ?? DEFAULT_MIN_BLOCK_HEIGHT_MM;
  const minFontSizeMm = Math.max(2.16, document.typography.minXHeightMm * 1.8);
  const overflowMarginMm = Math.max(0, options.overflowMarginMm ?? 0);
  const protectedAreas = options.protectedAreas ?? [];
  const accepted: PrepackagedLabelLayoutBlockV2[] = [];
  assertBoundedSearch(
    page,
    options.snapStepMm ?? DEFAULT_SNAP_STEP_MM,
    document.blocks[0]?.id ?? 'CUSTOM_SECTIONS',
  );

  for (const source of document.blocks) {
    const initialGeometry = clampGeometry(source.geometry, page, {
      minWidthMm,
      minHeightMm,
      snapStepMm: options.snapStepMm,
      overflowMarginMm,
    });
    const obstacles = [...protectedAreas, ...accepted.map((block) => block.geometry)];
    const geometry = findLegalPlacement(initialGeometry, page, obstacles, {
      minWidthMm,
      minHeightMm,
      snapStepMm: options.snapStepMm,
      blockGapMm: options.blockGapMm ?? 0,
      overflowMarginMm,
    });
    if (!geometry) {
      throw new LayoutUnsatisfiableError(
        source.id,
        `no non-overlapping placement remains on ${page.widthMm}×${page.heightMm}mm page`,
      );
    }
    accepted.push({
      ...source,
      required: source.required === true,
      visible: source.required ? true : source.visible,
      geometry,
      ...(source.typography
        ? {
            typography: {
              ...source.typography,
              fontSizeMm: Math.max(minFontSizeMm, source.typography.fontSizeMm),
              lineHeight: Math.max(1, source.typography.lineHeight),
            },
          }
        : {
            typography: {
              ...blockTypography(
                source.id,
                document.typography.baseFontSizeMm,
                document.typography.minXHeightMm,
              ),
            },
          }),
    });
  }
  const acceptedAssets: PrepackagedLabelLayoutAssetV2[] = [];
  for (const source of document.assets ?? []) {
    const minSize = minAssetSizeFor(source.kind, options);
    const initialGeometry = clampGeometry(source.geometry, page, {
      minWidthMm: minSize.widthMm,
      minHeightMm: minSize.heightMm,
      snapStepMm: options.snapStepMm,
      overflowMarginMm,
    });
    // A decorative shape is only ever clamped: it lives under the blocks,
    // never dislodges anything, and nothing dislodges it (spec §5.1).
    if (!collidesInLayout(source)) {
      acceptedAssets.push({
        ...source,
        required: source.required === true,
        visible: source.required ? true : source.visible,
        geometry: initialGeometry,
      });
      continue;
    }
    const obstacles = [
      ...protectedAreas,
      ...accepted.map((block) => collisionRectOf(block)),
      ...acceptedAssets.filter(collidesInLayout).map((asset) => collisionRectOf(asset)),
    ];
    const assetPlacementOptions = {
      minWidthMm: minSize.widthMm,
      minHeightMm: minSize.heightMm,
      snapStepMm: options.snapStepMm,
      blockGapMm: options.blockGapMm ?? 0,
      overflowMarginMm,
      ...(source.rotationDeg === undefined
        ? {}
        : {
            toCollisionRect: (geometry: LayoutRectMm) =>
              collisionRectMm(geometry, source.rotationDeg ?? 0),
          }),
    };
    // The requested size first; when the label is too full for it, a smaller
    // placement that keeps the asset's aspect ratio (`ASSET_SHRINK_FLOOR_RATIO`)
    // beats the alternative — refusing and making the operator's upload
    // silently unusable on the default label.
    const geometry =
      findLegalPlacement(initialGeometry, page, obstacles, assetPlacementOptions) ??
      findShrunkAssetPlacement(initialGeometry, page, obstacles, assetPlacementOptions);
    if (!geometry) {
      throw new LayoutUnsatisfiableError(
        accepted[0]?.id ?? 'CUSTOM_SECTIONS',
        `no non-overlapping placement remains for asset ${source.assetId}`,
      );
    }
    acceptedAssets.push({
      ...source,
      required: source.required === true,
      visible: source.required ? true : source.visible,
      geometry,
    });
  }
  return { ...document, blocks: accepted, assets: acceptedAssets };
}

export function removeLayoutBlock(
  document: PrepackagedLabelLayoutDocumentV2,
  blockId: PrepackagedLabelEditorBlockId,
): PrepackagedLabelLayoutDocumentV2 {
  const block = document.blocks.find((candidate) => candidate.id === blockId);
  if (!block || block.required) {
    return document;
  }
  return { ...document, blocks: document.blocks.filter((candidate) => candidate.id !== blockId) };
}

/** Mirrors removeLayoutBlock for decoration assets: mandatory (required)
 *  assets such as regulatory glyphs cannot be deleted, unknown ids are a
 *  no-op, everything else (free text, uploaded images) drops out of the
 *  document. */
export function removeLayoutAsset(
  document: PrepackagedLabelLayoutDocumentV2,
  assetId: string,
): PrepackagedLabelLayoutDocumentV2 {
  const asset = document.assets?.find((candidate) => candidate.assetId === assetId);
  if (!asset || asset.required) {
    return document;
  }
  return {
    ...document,
    assets: document.assets?.filter((candidate) => candidate.assetId !== assetId),
  };
}

export function updateLayoutBlockGeometry(
  document: PrepackagedLabelLayoutDocumentV2,
  blockId: PrepackagedLabelEditorBlockId,
  geometry: LayoutRectMm,
  options: LayoutSolverOptions = {},
): PrepackagedLabelLayoutDocumentV2 {
  const target = document.blocks.find((block) => block.id === blockId);
  if (!target) {
    return document;
  }
  const page = { widthMm: document.widthMm, heightMm: document.heightMm };
  const minWidthMm = options.minWidthMm ?? DEFAULT_MIN_BLOCK_WIDTH_MM;
  const minHeightMm = options.minHeightMm ?? DEFAULT_MIN_BLOCK_HEIGHT_MM;
  const snapStepMm = options.snapStepMm ?? DEFAULT_SNAP_STEP_MM;
  const overflowMarginMm = Math.max(0, options.overflowMarginMm ?? 0);
  assertBoundedSearch(page, snapStepMm, blockId);
  assertGridCanFitMinimum(
    canvasBounds(page, overflowMarginMm),
    snapStepMm,
    minWidthMm,
    minHeightMm,
    blockId,
  );
  const obstacles = [
    ...(options.protectedAreas ?? []),
    ...document.blocks
      .filter((block) => block.id !== blockId)
      .map((block) => collisionRectOf(block)),
    // Decorative shapes are never obstacles for a block: the block draws over
    // them and neither moves the other (spec §5.1).
    ...(document.assets ?? []).filter(collidesInLayout).map((asset) => collisionRectOf(asset)),
  ];
  // Con livelli (FREE) il posizionamento manuale può sovrapporsi: vale la
  // geometria clampata richiesta, niente ricerca di posto legale.
  const clamped = clampGeometry(geometry, page, {
    minWidthMm,
    minHeightMm,
    snapStepMm,
    overflowMarginMm,
  });
  if (options.allowOverlaps === true) {
    return {
      ...document,
      blocks: document.blocks.map((block) =>
        block.id === blockId ? { ...block, geometry: clamped, autoPlaced: false } : block,
      ),
    };
  }
  const placement = findLegalPlacement(clamped, page, obstacles, {
    minWidthMm,
    minHeightMm,
    snapStepMm,
    overflowMarginMm,
    blockGapMm: options.blockGapMm ?? 0,
    placementMode: options.placementMode,
  });
  if (!placement) {
    throw new LayoutUnsatisfiableError(
      blockId,
      `no non-overlapping placement remains on ${page.widthMm}×${page.heightMm}mm page`,
    );
  }
  return {
    ...document,
    blocks: document.blocks.map((block) =>
      block.id === blockId
        ? // The operator just explicitly repositioned/resized this exact
          // block: whatever machine placed it, that placement no longer owns
          // it alone. `downgradeStaleRequiredBlocks`
          // (`shared/src/label/required-label-blocks.ts`) must never remove
          // a block past this point without the operator seeing it happen.
          { ...block, geometry: placement, autoPlaced: false }
        : block,
    ),
  };
}

export function updateLayoutAssetGeometry(
  document: PrepackagedLabelLayoutDocumentV2,
  assetId: string,
  geometry: LayoutRectMm,
  options: LayoutSolverOptions = {},
): PrepackagedLabelLayoutDocumentV2 {
  const target = document.assets?.find((asset) => asset.assetId === assetId);
  if (!target) {
    return document;
  }
  const page = { widthMm: document.widthMm, heightMm: document.heightMm };
  const minSize = minAssetSizeFor(target.kind, options);
  const minWidthMm = minSize.widthMm;
  const minHeightMm = minSize.heightMm;
  const snapStepMm = options.snapStepMm ?? DEFAULT_SNAP_STEP_MM;
  const overflowMarginMm = Math.max(0, options.overflowMarginMm ?? 0);
  const errorBlockId = document.blocks[0]?.id ?? 'CUSTOM_SECTIONS';
  assertBoundedSearch(page, snapStepMm, errorBlockId);
  assertGridCanFitMinimum(
    canvasBounds(page, overflowMarginMm),
    snapStepMm,
    minWidthMm,
    minHeightMm,
    errorBlockId,
  );
  const clampedTarget = clampGeometry(geometry, page, {
    minWidthMm,
    minHeightMm,
    snapStepMm,
    overflowMarginMm,
  });
  // A decorative target is never relocated: skip the placement search and
  // return the clamp alone — it may sit under anything (spec §5.1).
  if (!collidesInLayout(target)) {
    return {
      ...document,
      assets: document.assets?.map((asset) =>
        asset.assetId === assetId ? { ...asset, geometry: clampedTarget } : asset,
      ),
    };
  }
  // Con livelli (FREE) l'asset va dove lo lascia l'operatore: la sovrapposizione
  // è ammessa e l'ordine di disegno è lo z-order scelto, niente ricerca.
  if (options.allowOverlaps === true) {
    return {
      ...document,
      assets: document.assets?.map((asset) =>
        asset.assetId === assetId ? { ...asset, geometry: clampedTarget } : asset,
      ),
    };
  }
  const obstacles = [
    ...(options.protectedAreas ?? []),
    ...document.blocks.map((block) => collisionRectOf(block)),
    ...(document.assets ?? [])
      .filter((asset) => asset.assetId !== assetId && collidesInLayout(asset))
      .map((asset) => collisionRectOf(asset)),
  ];
  const placement = findLegalPlacement(clampedTarget, page, obstacles, {
    minWidthMm,
    minHeightMm,
    snapStepMm,
    overflowMarginMm,
    blockGapMm: options.blockGapMm ?? 0,
    placementMode: options.placementMode,
    ...(target.rotationDeg === undefined
      ? {}
      : {
          toCollisionRect: (rect: LayoutRectMm) => collisionRectMm(rect, target.rotationDeg ?? 0),
        }),
  });
  if (!placement) {
    throw new LayoutUnsatisfiableError(
      errorBlockId,
      `no non-overlapping placement remains for asset ${assetId}`,
    );
  }
  return {
    ...document,
    assets: document.assets?.map((asset) =>
      asset.assetId === assetId ? { ...asset, geometry: placement } : asset,
    ),
  };
}

export const layoutRectToPixels = (rect: LayoutRectMm, dpi = DEFAULT_LAYOUT_DPI) => ({
  x: mmToPx(rect.xMm, dpi),
  y: mmToPx(rect.yMm, dpi),
  width: mmToPx(rect.widthMm, dpi),
  height: mmToPx(rect.heightMm, dpi),
});
